import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Col, Container, Row, Button } from "reactstrap";
import API from "../../utils/API";
import Loader from "../../components/Loader";
import dateFormat from "dateformat";
import DateRange from "../../components/formfields/DateRange";
import ProductMulti from "../../components/formfields/ProductMulti";
import RCIDByPublisher from "../../components/formfields/RCIDByPublisher";
import RCIDDisplay from "../../components/fields/RCID";
import SubIDByPublisher from "../../components/formfields/SubIDByPublisher";
import PublisherDisplay from "../../components/fields/Publisher";
import MoneyTotal from "../../components/fields/MoneyTotal";
import MoneyPub from "../../components/fields/MoneyPub";
import CSVDownload from "../../components/CsvDownload";
import SubIDDetail from "../../components/fields/SubIDDetail";
import { parseFilterValues } from "../../utils/helpers/filterHelper";
import { useHistory } from "react-router-dom";
import PublisherSelect from "../../components/formfields/PublisherSelect";
import SortableTable from "../../components/SortableTable";

const SubIDReport = () => {
  const history = useHistory();

  const defaultDate = useMemo(() => {
    return (
        dateFormat(new Date().setDate(new Date().getDate() - 6), "mmm d, yyyy") +
        " to " +
        dateFormat(new Date(), "mmm d, yyyy")
    );
  }, []);

  const defaultFiltrationState = useMemo(
  () => ({
    date: defaultDate,
    rcid: [],
    subid: [],
  }),
  [defaultDate]
  );

  const getParsedFilters = (searchString) => {
    const search = searchString !== undefined ? searchString : history.location.search;
    const urlParams = new URLSearchParams(search);
    const parsedParams = Object.fromEntries(urlParams.entries());
    const filtersParsed = parseFilterValues(parsedParams);
    return { ...defaultFiltrationState, ...filtersParsed };
  };

  const [filtration, setFiltration] = useState(() => {
    const urlParams = new URLSearchParams(history.location.search);
    const parsedParams = Object.fromEntries(urlParams.entries());
    const filtersParsed = parseFilterValues(parsedParams);
    return { ...defaultFiltrationState, ...filtersParsed };
  });
  const [appliedFiltration, setAppliedFiltration] = useState(null);
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const requestSeq = useRef(0);

  const loadResults = (filters) => {
    if (!filters?.date) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    API.post("/admin/subid/list", filters)
      .then((response) => {
        if (seq !== requestSeq.current) return;
        setData(response.data?.data?.report ?? []);
        setAppliedFiltration(filters);
      })
      .catch(() => {
        if (seq !== requestSeq.current) return;
        setData([]);
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setLoading(false);
      });
  };

  const filtersChanged = appliedFiltration !== null &&
    JSON.stringify(filtration) !== JSON.stringify(appliedFiltration);

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const next = getParsedFilters(location.search);
      setFiltration(next);
      if (next.publisher) setPub(next.publisher);
      else setPub(null);
    });
    return () => unlisten();
  }, [history]);

  useEffect(() => {
    if (!pub && filtration.publisher) {
      setPub(filtration.publisher);
    }
  }, [filtration.publisher, pub]);
  const summary = useMemo(() => {
    const stats = {
      leads: 0,
      matched: 0,
      unmatched: 0,
      flagged: 0,
      duplicate: 0,
      failed: 0,
      profit: 0,
      total: 0,
      pub: 0,
    };

    if (Array.isArray(data) && data.length > 0) {
      data.forEach((item) => {
        stats.leads += item.leads || 0;
        stats.matched += item.matched || 0;
        stats.unmatched += item.unmatched || 0;
        stats.flagged += item.flagged || 0;
        stats.duplicate += item.duplicate || 0;
        stats.failed += item.failed || 0;
        stats.total += parseFloat(item.total) || 0;
        stats.pub += parseFloat(item.pub) || 0;
      });
      stats.profit = stats.total - stats.pub;
    }

    return stats;
  }, [data]);

  // 4. Memoize Columns
  const columns = useMemo(
  () => [
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
    },
    {
      name: "Publisher",
      selector: (row) => row.publisher.name.toLowerCase(),
      cell: (row) => (
        <span>
          <PublisherDisplay publisher={row.publisher} />
        </span>
      ),
      sortable: true,
    },
    {
      name: "RCID",
      selector: (row) => row.rcid && row.rcid.id,
      cell: (row) => (row.rcid ? <RCIDDisplay id={row.rcid.id} /> : ""),
      sortable: true,
    },
    {
      name: "SubID",
      selector: (row) =>
        row.subid ? row.subid.subid.toLowerCase() : "no subid",
      cell: (row) => <SubIDDetail subid={row.subid} item={row} />,
      sortable: true,
    },
    {
      name: "Total Leads",
      selector: (row) => row.leads,
      cell: (row) => <span className="font-weight-bold">{row.leads}</span>,
      sortable: true,
    },
    {
      name: "Matched",
      selector: (row) => row.matched,
      cell: (row) => (
        <span className="text-success font-weight-bold">{row.matched}</span>
      ),
      sortable: true,
    },
    {
      name: "Unmatched",
      selector: (row) => row.unmatched,
      sortable: true,
    },
    {
      name: "Flagged",
      selector: (row) => row.flagged,
      cell: (row) => <span className="text-primary">{row.flagged}</span>,
      sortable: true,
    },
    {
      name: "Failed",
      selector: (row) => row.failed,
      sortable: true,
    },
    {
      name: "Success Rate",
      selector: (row) => row.successRate,
      cell: (row) => (row.successRate ? row.successRate : "0") + "%",
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) => row.total && parseFloat(row.total),
      cell: (row) => (
        <span className="text-success">
          {row.total ? (
            <MoneyTotal money={row.total} moneyOriginal={row.totaloriginal} />
          ) : (
            "$0"
          )}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Rev Per Lead",
      selector: (row) => row.revPerLead,
      cell: (row) => "$" + row.revPerLead,
      sortable: true,
    },
    {
      name: "Publisher",
      selector: (row) => row.pub && row.pub,
      cell: (row) => (
        <span>
          {row.pub ? (
            <MoneyPub money={row.pub} moneyOriginal={row.puboriginal} />
          ) : (
            "$0"
          )}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Profit",
      selector: (row) => row.total && row.pub && row.total - row.pub,
      cell: (row) => (
        <span>
          {row.total || row.pub ? (
            <MoneyTotal money={row.total - row.pub} />
          ) : (
            "$0"
          )}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Margin",
      selector: (row) => row.margin,
      cell: (row) => <span>{row.margin ? row.margin : "0"}%</span>,
      sortable: true,
    },
  ],
  []
  );

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const isPublisherCleared = (value) => {
    if (!value) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  };

  const changeData = (name, value) => {
    let updatedFiltration = { ...filtration };

    if (name === "publisher" && isPublisherCleared(value)) {
      setPub(null);
      updatedFiltration = {
        ...filtration,
        publisher: null,
        rcid: [],
        subid: [],
      };
    } else if (name === "publisher" && pub !== value) {
      setPub(value);
      updatedFiltration = {
        ...filtration,
        publisher: value,
        rcid: [],
        subid: [],
      };
    } else {
      updatedFiltration = { ...filtration, [name]: value };
    }

    setFiltration(updatedFiltration);

    const searchParams = new URLSearchParams();
    Object.entries(updatedFiltration).forEach(([key, val]) => {
      if (val === undefined || val === null) return;
      if (Array.isArray(val)) {
        if (val.length) searchParams.set(key, val.map(String).join(","));
      } else {
        searchParams.set(key, String(val));
      }
    });
    history.push({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
  };

  return (
    <Container fluid className="p-0">
      {filtersChanged && (
        <style>{`
          @keyframes show-results-pulse {
            0%, 100% { box-shadow: 0 0 12px 2px rgba(0, 123, 255, 0.5); }
            50% { box-shadow: 0 0 20px 5px rgba(0, 123, 255, 0.85); }
          }
        `}</style>
      )}
      <div className="row">
        <div className="col text-left">
          <h1 className="h3 mb-3">SubID Report</h1>
        </div>
        {!loading && (
          <>
            <div className="col-auto d-inline-flex">
              SubID Report -{" "}
              <CSVDownload
                url="/admin/subid/report/subidsummaryreportcsv"
                filename="Sub ID Report"
                filtration={appliedFiltration ?? filtration}
              />
            </div>
            <div className="d-inline-flex">
              Complete Lead List -{" "}
              <CSVDownload
                url="/admin/lead/detailreportfulldatacsv"
                filename="Sub ID Report - Lead List"
                filtration={appliedFiltration ?? filtration}
              />
            </div>
          </>
        )}
      </div>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Row form>
            <Col md={3}>
              <DateRange
                onChange={changeData}
                name={"date"}
                value={filtration?.date}
              />
            </Col>
            <Col md={3}>
              <PublisherSelect
                name={"publisher"}
                onChange={changeData}
                value={filtration.publisher}
                placeholder={"Publisher"}
                required={false}
                isClearable={true}
                isMulti={false}
              />
            </Col>
            <Col md={3}>
              <ProductMulti
                name={"product"}
                onChange={changeData}
                value={filtration.product}
              />
            </Col>
            <Col md={3}>
              <RCIDByPublisher
                name={"rcid"}
                onChange={changeData}
                publisher={filtration.publisher}
                value={filtration.rcid}
              />
            </Col>
          </Row>
          <Row form className="mt-1 mb-2">
            <Col md={3}>
              <SubIDByPublisher
                name={"subid"}
                onChange={changeData}
                publisher={filtration.publisher}
                value={filtration.subid}
              />
            </Col>
          </Row>
          <div className="mb-3 text-center mt-4">
            <Button
              color="primary"
              onClick={() => loadResults(filtration)}
              style={filtersChanged ? {
                boxShadow: "0 0 16px 3px rgba(0, 123, 255, 0.7)",
                animation: "show-results-pulse 1.5s ease-in-out infinite",
              } : undefined}
            >
              Show results
            </Button>
          </div>
        </>
      )}
      {!loading && data && data.length > 0 ? (
        <>
          <SortableTable
              columns={columns}
              data={data}
              pagination={true}
              paginationPerPage={100}
              paginationRowsPerPageOptions={[100, 200, 500, 1000]}
              onChangePage={scrollToTop}
              onChangeRowsPerPage={scrollToTop}
          />

          <Card className="flex-fill w-100 p-3">
            <h4>Summary:</h4>
            <ul className="bull list-unstyled mb-0">
              <li>
                Leads: <strong>{summary.leads}</strong>
              </li>
              <li>
                Matched: <strong>{summary.matched}</strong>
              </li>
              <li>
                Unmatched: <strong>{summary.unmatched}</strong>
              </li>
              <li>
                Flagged: <strong>{summary.flagged}</strong>
              </li>
              <li>
                Failed: <strong>{summary.failed}</strong>
              </li>
              <li>
                Revenue:{" "}
                {summary.total ? <MoneyTotal money={summary.total} /> : "$0"}
              </li>
              <li>
                Publisher revenue:{" "}
                {summary.pub ? <MoneyPub money={summary.pub} /> : "$0"}
              </li>
              <li>
                Profit:{" "}
                {summary.profit ? <MoneyTotal money={summary.profit} /> : "$0"}
              </li>
              <li>
                Margin:{" "}
                {summary.total ? parseFloat((100 * summary.profit) / summary.total).toFixed(2) : "0"}
                %
              </li>
              <li>
                Success rate:{" "}
                {summary.leads ? parseFloat((100 * summary.matched) / summary.leads).toFixed(2) : "0"}
                %
              </li>
            </ul>
          </Card>
        </>
      ) : (
        !loading && (
          <CardBody className="text-center mt-2">
            {appliedFiltration === null ? (
              <h5>Click &quot;Show results&quot; to load data</h5>
            ) : (
              <h5>Data not found</h5>
            )}
          </CardBody>
        )
      )}
    </Container>
  );
};

export default SubIDReport;
